import React, { useEffect, useRef, useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { getFitTestsPaginado, updateFitTest, reagendarFitTest, standbyFitTest, confirmarFitTest, recusarFitTest, cancelarFitTest } from '../../api/fitTests';
import useSortable from '../../hooks/useSortable';
import { uploadCertificado, downloadCertificado, deleteCertificado } from '../../api/certificados';
import { getLocais } from '../../api/locais';
import { getEquipamentos } from '../../api/equipamentos';
import { TIPOS_TESTE, GRUPOS_TESTE, NIVEIS_ATENUACAO, tipoIcon, isRespiratorio } from '../../utils/fitTestOpcoes';
import { ordinal } from '../../utils/ciclo';
import { todayIso, addOneYear } from '../../utils/date';
import Paginacao from '../../components/common/Paginacao';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const TIPOS_RECUSA = [
  'Data indisponível',
  'Feriado / ponto facultativo',
  'Impossibilidade operacional',
  'Capacidade excedida',
  'Equipamento em manutenção',
  'Outro',
];

const today = todayIso;

export default function FitTests() {
  const { isFenix, isAdmin } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState({ tipo: '', resultado: '', situacao: '', busca: '' });
  const [buscaInput, setBuscaInput] = useState('');
  const buscaDebounced = useDebounce(buscaInput, 400);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [emAnaliseTotal, setEmAnaliseTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cancelLoading, setCancelLoading] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resultId, setResultId] = useState(null);
  const [resultForm, setResultForm] = useState({ resultado: 'APROVADO', data_realizada: today() });
  const [recusaId, setRecusaId] = useState(null);
  const [recusaForm, setRecusaForm] = useState({ tipo_recusa: TIPOS_RECUSA[0], descricao_recusa: '', data_sugerida: '' });
  const [standbyLoading, setStandbyLoading] = useState(null);
  const [locais, setLocais] = useState([]);
  const [certLoading, setCertLoading] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetId = useRef(null);
  const [reagendarId, setReagendarId] = useState(null);
  const [reagendarForm, setReagendarForm] = useState({ data_agendada: '', data_validade: '' });
  const [confirmarEquipId, setConfirmarEquipId] = useState(null); // id do teste aguardando seleção de equipamento
  const [confirmarEquip, setConfirmarEquip] = useState('');
  const [equipamentos, setEquipamentos] = useState([]);
  const navigate = useNavigate();

  // Carga paginada do servidor
  useEffect(() => {
    setLoading(true);
    const params = { skip, limit };
    if (filtro.situacao) params.situacao = filtro.situacao;
    if (filtro.tipo)     params.tipo_teste = filtro.tipo;
    if (filtro.resultado) params.resultado = filtro.resultado;
    if (filtro.busca)    params.busca = filtro.busca;
    getFitTestsPaginado(params)
      .then(({ items, total: t }) => { setTests(items); setTotal(t); })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [skip, limit, filtro.situacao, filtro.tipo, filtro.resultado, filtro.busca, refreshKey]);

  // Conta solicitações em análise (banner de alerta)
  useEffect(() => {
    if (isFenix) {
      getFitTestsPaginado({ situacao: 'em_analise', limit: 1 })
        .then(({ total: t }) => setEmAnaliseTotal(t));
    }
  // eslint-disable-next-line
  }, [isFenix, refreshKey]);

  useEffect(() => {
    getLocais().then(setLocais);
    getEquipamentos().then(setEquipamentos);
  }, []);

  // Sincroniza busca debounced com filtro (evita request a cada tecla)
  useEffect(() => {
    setFiltro(f => ({ ...f, busca: buscaDebounced }));
    setSkip(0);
  // eslint-disable-next-line
  }, [buscaDebounced]);

  const setFiltroComReset = (key, value) => {
    setSkip(0);
    setFiltro(f => ({ ...f, [key]: value }));
  };

  const load = () => setRefreshKey(k => k + 1);

  // Ordena a página atual no cliente (não troca de página)
  const { sorted: ordenados, Th } = useSortable(tests, 'data_agendada', 'desc');

  // Props de paginação derivadas do estado de servidor
  const pagina = limit > 0 ? Math.floor(skip / limit) + 1 : 1;
  const totalPaginas = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  const pagProps = {
    pagina,
    totalPaginas,
    inicio: total > 0 ? skip + 1 : 0,
    fim: Math.min(skip + limit, total),
    total,
    porPagina: limit,
    setPagina: (n) => setSkip((n - 1) * limit),
    setPorPagina: (n) => { setLimit(n); setSkip(0); },
  };


  const closeAll = () => { setEditId(null); setResultId(null); setRecusaId(null); setReagendarId(null); setConfirmarEquipId(null); setConfirmarEquip(''); };

  const startEdit = (t) => {
    closeAll();
    setEditId(t.id_fit_test);
    setEditForm({
      id_equipamento: t.id_equipamento || '',
      metodo: t.metodo || '', data_agendada: t.data_agendada || '',
      data_realizada: t.data_realizada || '', duracao_teste: t.duracao_teste || '',
      resultado: t.resultado || '', data_validade: t.data_validade || '',
      observacoes: t.observacoes || '',
      exposicao_db: t.exposicao_db || '',
      nap_resultante: t.nap_resultante || '',
      nivel_atenuacao: t.nivel_atenuacao || '',
      faz_parte_brigada: t.faz_parte_brigada ?? false,
      faz_parte_controle_poco: t.faz_parte_controle_poco ?? false,
      grupo_teste: t.grupo_teste || '',
      id_local: t.id_local || '',
      _tipo_teste: t.tipo_teste,
    });
  };

  const handleUploadClick = (id) => {
    uploadTargetId.current = id;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetId.current) return;
    e.target.value = '';
    const id = uploadTargetId.current;
    setCertLoading(id);
    try {
      await uploadCertificado(id, file);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao fazer upload do certificado');
    } finally {
      setCertLoading(null);
    }
  };

  const handleDownload = async (t) => {
    setCertLoading(t.id_fit_test);
    try { await downloadCertificado(t.id_fit_test, t.certificado_nome); }
    catch { alert('Erro ao baixar o certificado'); }
    finally { setCertLoading(null); }
  };

  const handleDeleteCert = async (t) => {
    if (!window.confirm(`Excluir o certificado "${t.certificado_nome}"?`)) return;
    setCertLoading(t.id_fit_test);
    try { await deleteCertificado(t.id_fit_test); load(); }
    catch { alert('Erro ao excluir o certificado'); }
    finally { setCertLoading(null); }
  };

  const handleCancelar = async (t) => {
    if (!window.confirm(`Cancelar a solicitação de "${t.funcionario_empresa?.funcionario?.nome}"?`)) return;
    setCancelLoading(t.id_fit_test);
    try { await cancelarFitTest(t.id_fit_test); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Erro ao cancelar'); }
    finally { setCancelLoading(null); }
  };

  const handleStandby = async (id) => {
    setStandbyLoading(id);
    try { await standbyFitTest(id); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Erro'); }
    finally { setStandbyLoading(null); }
  };

  const startResult = (t) => {
    closeAll();
    setResultId(t.id_fit_test);
    setResultForm({
      resultado: t.resultado || 'APROVADO',
      data_realizada: today(),
      metodo: t.metodo || '',
      duracao_teste: t.duracao_teste || '',
      _tipo_teste: t.tipo_teste,
      exposicao_db: t.exposicao_db || '',
      nap_resultante: t.nap_resultante || '',
      nivel_atenuacao: t.nivel_atenuacao || '',
    });
  };

  const startRecusa = (t) => {
    closeAll();
    setRecusaId(t.id_fit_test);
    setRecusaForm({ tipo_recusa: TIPOS_RECUSA[0], descricao_recusa: '' });
  };

  const startReagendar = (t) => {
    closeAll();
    setReagendarId(t.id_fit_test);
    setReagendarForm({ data_agendada: t.data_agendada || '', data_validade: t.data_validade || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const payload = {
      id_equipamento: editForm.id_equipamento || null,
      metodo: editForm.metodo || null, data_agendada: editForm.data_agendada || null,
      data_realizada: editForm.data_realizada || null, duracao_teste: editForm.duracao_teste || null,
      resultado: editForm.resultado || null, data_validade: editForm.data_validade || null,
      observacoes: editForm.observacoes || null,
      exposicao_db: !isRespiratorio(editForm._tipo_teste) ? editForm.exposicao_db || null : null,
      nap_resultante: !isRespiratorio(editForm._tipo_teste) ? editForm.nap_resultante || null : null,
      nivel_atenuacao: !isRespiratorio(editForm._tipo_teste) ? editForm.nivel_atenuacao || null : null,
      faz_parte_brigada: isRespiratorio(editForm._tipo_teste) ? editForm.faz_parte_brigada : null,
      faz_parte_controle_poco: isRespiratorio(editForm._tipo_teste) ? editForm.faz_parte_controle_poco : null,
      grupo_teste: editForm.grupo_teste || null,
      id_local: editForm.id_local || null,
    };
    await updateFitTest(editId, payload);
    setEditId(null); load();
  };

  const handleResult = async (e) => {
    e.preventDefault();
    const payload = {
      resultado: resultForm.resultado,
      data_realizada: resultForm.data_realizada,
      metodo: resultForm.metodo || null,
      duracao_teste: resultForm.duracao_teste || null,
    };
    if (resultForm._tipo_teste === 'auditivo') {
      payload.exposicao_db = resultForm.exposicao_db || null;
      payload.nap_resultante = resultForm.nap_resultante || null;
      payload.nivel_atenuacao = resultForm.nivel_atenuacao || null;
    }
    await updateFitTest(resultId, payload);
    setResultId(null); load();
  };

  const handleReagendar = async (e) => {
    e.preventDefault();
    await reagendarFitTest(reagendarId, {
      data_agendada: reagendarForm.data_agendada,
      data_validade: reagendarForm.data_validade || null,
    });
    setReagendarId(null); load();
  };

  const handleConfirmar = async (id, equipId = null) => {
    try {
      await confirmarFitTest(id, equipId);
      setConfirmarEquipId(null);
      setConfirmarEquip('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao confirmar agendamento');
    }
  };

  const handleRecusar = async (e) => {
    e.preventDefault();
    try {
      await recusarFitTest(recusaId, { ...recusaForm, data_sugerida: recusaForm.data_sugerida || null });
      setRecusaId(null); load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao recusar agendamento');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {/* Input oculto para upload — compartilhado entre todas as linhas */}
      <input ref={fileInputRef} type="file" accept=".pdf,application/pdf"
        style={{ display: 'none' }} onChange={handleFileSelected} />

      <PageHeader
        title="Fit Tests"
        subtitle="Histórico completo de testes"
        action={
          isFenix ? (
            <button className="btn-datafit" onClick={() => navigate('/fit-tests/novo')}>
              <i className="bi bi-plus-circle me-2"></i>Novo teste
            </button>
          ) : (
            <button className="btn-datafit" onClick={() => navigate('/solicitar-agendamento')}>
              <i className="bi bi-calendar-plus me-2"></i>Solicitar agendamento
            </button>
          )
        }
      />

      {isFenix && emAnaliseTotal > 0 && (
        <div className="alert d-flex align-items-center gap-2 mb-3"
          style={{ background: '#fff3cd', border: '1px solid #ffc107', color: '#664d03' }}>
          <i className="bi bi-hourglass-split fs-5"></i>
          <span>
            <strong>{emAnaliseTotal} solicitação(ões)</strong> aguardando análise.{' '}
            <button className="btn btn-link btn-sm p-0" style={{ color: '#664d03' }}
              onClick={() => setFiltroComReset('situacao', 'em_analise')}>
              Ver agora
            </button>
          </span>
        </div>
      )}

      <div className="card-datafit">
        <div className="p-3 border-bottom d-flex gap-2 flex-wrap">
          <input type="text" className="form-control form-control-sm" style={{ width: 220 }}
            placeholder="Buscar funcionário..."
            value={buscaInput} onChange={e => setBuscaInput(e.target.value)} />
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filtro.situacao} onChange={e => setFiltroComReset('situacao', e.target.value)}>
            <option value="">Todas as situações</option>
            <option value="em_analise">Em análise</option>
            <option value="confirmado">Confirmado</option>
            <option value="standby">Standby</option>
            <option value="recusado">Recusado</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filtro.tipo} onChange={e => setFiltroComReset('tipo', e.target.value)}>
            <option value="">Todos os tipos</option>
            {TIPOS_TESTE.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filtro.resultado} onChange={e => setFiltroComReset('resultado', e.target.value)}>
            <option value="">Todos os resultados</option>
            <option value="APROVADO">Aprovado</option>
            <option value="REPROVADO">Reprovado</option>
            <option value="REFAZER">Refazer</option>
          </select>
        </div>

        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr>
                <Th field="numero_ciclo">Ciclo</Th>
                <Th field="tipo_teste">Tipo</Th>
                <Th field="funcionario_empresa.funcionario.nome">Funcionário / Empresa</Th>
                <th>Equipamento</th>
                <Th field="data_agendada">Data agendada</Th>
                <Th field="data_realizada">Data realizada</Th>
                <Th field="data_validade">Validade</Th>
                <Th field="resultado">Resultado</Th>
                <Th field="situacao">Situação</Th>
                <th>Cert.</th><th></th>
              </tr>
            </thead>
            <tbody>
              {ordenados.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-muted py-4">Nenhum teste encontrado</td></tr>
              ) : ordenados.map(t => (
                <React.Fragment key={t.id_fit_test}>
                  <tr>
                    <td>
                      <span className="badge rounded-pill" style={{ background: 'var(--main-color)', color: '#fff' }}>
                        {ordinal(t.numero_ciclo)}
                      </span>
                    </td>
                    <td>
                      <i className={`bi ${tipoIcon(t.tipo_teste)} me-1`}></i>
                      {t.tipo_teste}
                      {t.grupo_teste && <span className="badge bg-secondary ms-1" style={{ fontSize: '.65rem' }}>{t.grupo_teste}</span>}
                    </td>
                    <td>
                      <div className="fw-500">{t.funcionario_empresa?.funcionario?.nome || '—'}</div>
                      <div className="small text-muted">
                        {t.funcionario_empresa?.empresa?.nome_fantasia || t.funcionario_empresa?.empresa?.razao_social || '—'}
                        {t.funcionario_empresa?.cargo ? ` · ${t.funcionario_empresa.cargo}` : ''}
                      </div>
                    </td>
                    <td>
                      <div>{t.equipamento?.ca}</div>
                      <div className="small text-muted">{t.equipamento?.tipo_mascara}</div>
                    </td>
                    <td>{t.data_agendada || '—'}</td>
                    <td>{t.data_realizada || '—'}</td>
                    <td>{t.data_validade || '—'}</td>
                    <td><StatusBadge status={t.resultado?.toLowerCase()} /></td>
                    <td><StatusBadge status={t.situacao} /></td>
                    {/* Certificado */}
                    <td className="text-center">
                      {certLoading === t.id_fit_test ? (
                        <span className="spinner-border spinner-border-sm text-muted" />
                      ) : t.certificado_nome ? (
                        <div className="d-flex gap-1 justify-content-center">
                          <button className="btn btn-sm btn-outline-success" title={`Baixar: ${t.certificado_nome}`}
                            onClick={() => handleDownload(t)}>
                            <i className="bi bi-download"></i>
                          </button>
                          {isFenix && (
                            <>
                              <button className="btn btn-sm btn-outline-primary" title="Substituir certificado"
                                onClick={() => handleUploadClick(t.id_fit_test)}>
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger" title="Excluir certificado"
                                onClick={() => handleDeleteCert(t)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        isFenix ? (
                          <button className="btn btn-sm btn-outline-secondary" title="Fazer upload do certificado"
                            onClick={() => handleUploadClick(t.id_fit_test)}>
                            <i className="bi bi-upload"></i>
                          </button>
                        ) : <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        {/* Botões fenix para em_analise */}
                        {isFenix && t.situacao === 'em_analise' && (
                          <>
                            <button className="btn btn-sm btn-success" title="Confirmar agendamento"
                              onClick={() => {
                                if (t.id_equipamento) {
                                  handleConfirmar(t.id_fit_test);
                                } else {
                                  closeAll();
                                  setConfirmarEquipId(t.id_fit_test);
                                  setConfirmarEquip('');
                                }
                              }}>
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button className="btn btn-sm btn-danger" title="Recusar agendamento"
                              onClick={() => recusaId === t.id_fit_test ? setRecusaId(null) : startRecusa(t)}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        )}
                        {/* Botão standby — confirmado sem resultado */}
                        {isFenix && t.situacao === 'confirmado' && !t.data_realizada && (
                          <button className="btn btn-sm btn-outline-secondary" title="Colocar em Standby"
                            disabled={standbyLoading === t.id_fit_test}
                            onClick={() => handleStandby(t.id_fit_test)}>
                            <i className="bi bi-pause-circle"></i>
                          </button>
                        )}
                        {/* Reativar standby */}
                        {isFenix && t.situacao === 'standby' && (
                          <button className="btn btn-sm btn-outline-success" title="Reativar agendamento"
                            onClick={() => handleConfirmar(t.id_fit_test)}>
                            <i className="bi bi-play-circle"></i>
                          </button>
                        )}
                        {/* Botão inserir resultado — confirmado e ainda não realizado (ou admin) */}
                        {isFenix && t.situacao === 'confirmado' && (isAdmin || !t.data_realizada) && (
                          <button className="btn btn-sm btn-outline-success" title="Inserir resultado"
                            onClick={() => resultId === t.id_fit_test ? setResultId(null) : startResult(t)}>
                            <i className="bi bi-patch-check-fill"></i>
                          </button>
                        )}
                        {/* Botão editar — apenas admin */}
                        {isAdmin && (
                          <button className="btn btn-sm btn-outline-primary" title="Editar (admin)"
                            onClick={() => editId === t.id_fit_test ? setEditId(null) : startEdit(t)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {/* Botão cancelar — cliente para em_analise e recusado */}
                        {!isFenix && ['em_analise', 'recusado'].includes(t.situacao) && (
                          <button className="btn btn-sm btn-outline-danger" title="Cancelar solicitação"
                            disabled={cancelLoading === t.id_fit_test}
                            onClick={() => handleCancelar(t)}>
                            {cancelLoading === t.id_fit_test
                              ? <span className="spinner-border spinner-border-sm" />
                              : <i className="bi bi-x-circle"></i>}
                          </button>
                        )}
                        {/* Botão reagendar — cliente para em_analise e recusado */}
                        {!isFenix && ['em_analise', 'recusado'].includes(t.situacao) && (
                          <button className="btn btn-sm btn-outline-warning" title="Alterar data solicitada"
                            onClick={() => reagendarId === t.id_fit_test ? setReagendarId(null) : startReagendar(t)}>
                            <i className="bi bi-calendar-event"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Linha de seleção de equipamento para confirmar */}
                  {confirmarEquipId === t.id_fit_test && (
                    <tr key={`confirmar-equip-${t.id_fit_test}`} className="table-success">
                      <td colSpan={11} className="p-3">
                        <div className="d-flex align-items-end gap-3 flex-wrap">
                          <div>
                            <label className="form-label small mb-1 fw-500">
                              <i className="bi bi-box-seam me-1"></i>
                              Selecione o equipamento para confirmar
                            </label>
                            <select className="form-select form-select-sm" style={{ width: 320 }}
                              value={confirmarEquip}
                              onChange={e => setConfirmarEquip(e.target.value)}>
                              <option value="">Selecione...</option>
                              {equipamentos
                                .filter(eq => eq.tipo_teste === t.tipo_teste)
                                .map(eq => (
                                  <option key={eq.id_equipamento} value={eq.id_equipamento}>
                                    {eq.ca} — {eq.tipo_mascara}{eq.fabricante ? ` (${eq.fabricante})` : ''}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <button className="btn btn-sm btn-success" disabled={!confirmarEquip}
                            onClick={() => handleConfirmar(t.id_fit_test, confirmarEquip)}>
                            <i className="bi bi-check-lg me-1"></i>Confirmar
                          </button>
                          <button className="btn btn-sm btn-outline-secondary"
                            onClick={() => { setConfirmarEquipId(null); setConfirmarEquip(''); }}>
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Linha de recusa */}
                  {recusaId === t.id_fit_test && (
                    <tr key={`recusa-${t.id_fit_test}`} className="table-danger">
                      <td colSpan={11} className="p-3">
                        <form onSubmit={handleRecusar} className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small mb-1 fw-500">Motivo da recusa *</label>
                            <select className="form-select form-select-sm" required
                              value={recusaForm.tipo_recusa}
                              onChange={e => setRecusaForm({ ...recusaForm, tipo_recusa: e.target.value })}>
                              {TIPOS_RECUSA.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small mb-1 fw-500">Descrição complementar</label>
                            <input type="text" className="form-control form-control-sm"
                              placeholder="Detalhes adicionais (opcional)"
                              value={recusaForm.descricao_recusa}
                              onChange={e => setRecusaForm({ ...recusaForm, descricao_recusa: e.target.value })} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small mb-1 fw-500">Data sugerida <span className="text-muted fw-normal">(opcional)</span></label>
                            <input type="date" className="form-control form-control-sm"
                              value={recusaForm.data_sugerida}
                              onChange={e => setRecusaForm({ ...recusaForm, data_sugerida: e.target.value })} />
                          </div>
                          <div className="col-md-2 d-flex align-items-end gap-2">
                            <button type="submit" className="btn btn-sm btn-danger">Recusar</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary"
                              onClick={() => setRecusaId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}

                  {/* Linha de resultado */}
                  {resultId === t.id_fit_test && (
                    <tr key={`result-${t.id_fit_test}`} className="table-success">
                      <td colSpan={11} className="p-3">
                        <form onSubmit={handleResult} className="row g-2 align-items-end">
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Resultado</label>
                            <select className="form-select form-select-sm" style={{ width: 150 }}
                              value={resultForm.resultado}
                              onChange={e => setResultForm({ ...resultForm, resultado: e.target.value })}>
                              <option value="APROVADO">Aprovado</option>
                              <option value="REPROVADO">Reprovado</option>
                                                <option value="REFAZER">Refazer</option>
                            </select>
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Data realizada</label>
                            <input type="date" className="form-control form-control-sm" style={{ width: 160 }}
                              value={resultForm.data_realizada}
                              min={isAdmin ? undefined : today()}
                              onChange={e => setResultForm({ ...resultForm, data_realizada: e.target.value })} />
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Método</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 140 }}
                              placeholder="Ex: ABNT NR15"
                              value={resultForm.metodo}
                              onChange={e => setResultForm({ ...resultForm, metodo: e.target.value })} />
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Duração</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 100 }}
                              placeholder="Ex: 45min"
                              value={resultForm.duracao_teste}
                              onChange={e => setResultForm({ ...resultForm, duracao_teste: e.target.value })} />
                          </div>

                          {/* Campos auditivos — só aparecem se o teste for auditivo */}
                          {t.tipo_teste === 'auditivo' && (
                            <>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">Exposição dB(A)</label>
                                <input type="number" step="0.1" className="form-control form-control-sm" style={{ width: 120 }}
                                  placeholder="Ex: 78.5"
                                  value={resultForm.exposicao_db}
                                  onChange={e => setResultForm({ ...resultForm, exposicao_db: e.target.value })} />
                              </div>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">NAP resultante</label>
                                <input type="number" step="0.1" className="form-control form-control-sm" style={{ width: 120 }}
                                  placeholder="Ex: 23.2"
                                  value={resultForm.nap_resultante}
                                  onChange={e => setResultForm({ ...resultForm, nap_resultante: e.target.value })} />
                              </div>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">Nível de atenuação</label>
                                <select className="form-select form-select-sm" style={{ width: 220 }}
                                  value={resultForm.nivel_atenuacao}
                                  onChange={e => setResultForm({ ...resultForm, nivel_atenuacao: e.target.value })}>
                                  <option value="">—</option>
                                  {NIVEIS_ATENUACAO.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </div>
                            </>
                          )}

                          <div className="col-auto d-flex gap-2">
                            <button type="submit" className="btn btn-sm btn-success">
                              <i className="bi bi-check-lg me-1"></i>Confirmar
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary"
                              onClick={() => setResultId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}

                  {/* Linha de reagendamento — para cliente alterar data */}
                  {reagendarId === t.id_fit_test && (
                    <tr key={`reagendar-${t.id_fit_test}`} className="table-warning">
                      <td colSpan={11} className="p-3">
                        {t.situacao === 'recusado' && (
                          <div className="alert alert-warning py-1 small mb-2">
                            <i className="bi bi-arrow-counterclockwise me-2"></i>
                            Alterar a data irá reenviar a solicitação para análise da equipe Fenix.
                          </div>
                        )}
                        <form onSubmit={handleReagendar} className="d-flex gap-3 flex-wrap align-items-end">
                          <div>
                            <label className="form-label small mb-1 fw-500">Nova data desejada *</label>
                            <input type="date" className="form-control form-control-sm" style={{ width: 180 }}
                              value={reagendarForm.data_agendada} required
                              onChange={e => setReagendarForm({
                                data_agendada: e.target.value,
                                data_validade: addOneYear(e.target.value),
                              })} />
                          </div>
                          <div>
                            <label className="form-label small mb-1 fw-500">Validade prevista</label>
                            <input type="date" className="form-control form-control-sm" style={{ width: 180 }}
                              value={reagendarForm.data_validade}
                              onChange={e => setReagendarForm({ ...reagendarForm, data_validade: e.target.value })} />
                          </div>
                          <button type="submit" className="btn btn-sm btn-warning">
                            <i className="bi bi-send me-1"></i>Confirmar alteração
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-secondary"
                            onClick={() => setReagendarId(null)}>Cancelar</button>
                        </form>
                      </td>
                    </tr>
                  )}

                  {/* Linha de recusa exibida — para cliente ver motivo */}
                  {t.situacao === 'recusado' && (
                    <tr key={`recusado-info-${t.id_fit_test}`} className="table-danger">
                      <td colSpan={11} className="px-4 py-2">
                        <div className="d-flex flex-wrap gap-3 align-items-start small">
                          <div>
                            <span className="fw-bold text-danger"><i className="bi bi-x-octagon me-1"></i>Motivo da recusa:</span>
                            <span className="ms-2">{t.tipo_recusa}</span>
                          </div>
                          {t.descricao_recusa && (
                            <div>
                              <span className="fw-bold">Detalhe:</span>
                              <span className="ms-2 text-muted">{t.descricao_recusa}</span>
                            </div>
                          )}
                          {t.data_sugerida && (
                            <div>
                              <span className="fw-bold text-success"><i className="bi bi-calendar-check me-1"></i>Data sugerida pela Fenix:</span>
                              <span className="ms-2 fw-bold text-success">{t.data_sugerida}</span>
                            </div>
                          )}
                          {!isFenix && (
                            <span className="text-muted fst-italic">Use o botão 📅 para solicitar nova data ou ✕ para cancelar.</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Linha de edição completa */}
                  {editId === t.id_fit_test && (
                    <tr key={`edit-${t.id_fit_test}`} className="table-light">
                      <td colSpan={11} className="p-3">
                        <form onSubmit={handleEdit} className="row g-2">
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Data agendada</label>
                            <input type="date" className="form-control form-control-sm"
                              value={editForm.data_agendada}
                              onChange={e => setEditForm({ ...editForm, data_agendada: e.target.value })} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Data realizada</label>
                            <input type="date" className="form-control form-control-sm"
                              value={editForm.data_realizada}
                              onChange={e => setEditForm({ ...editForm, data_realizada: e.target.value })} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Validade</label>
                            <input type="date" className="form-control form-control-sm"
                              value={editForm.data_validade}
                              onChange={e => setEditForm({ ...editForm, data_validade: e.target.value })} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Resultado</label>
                            <select className="form-select form-select-sm"
                              value={editForm.resultado}
                              onChange={e => setEditForm({ ...editForm, resultado: e.target.value })}>
                              <option value="">Pendente</option>
                              <option value="APROVADO">Aprovado</option>
                              <option value="REPROVADO">Reprovado</option>
                              <option value="REFAZER">Refazer</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Método</label>
                            <input type="text" className="form-control form-control-sm"
                              value={editForm.metodo}
                              onChange={e => setEditForm({ ...editForm, metodo: e.target.value })} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small mb-1">Duração</label>
                            <input type="text" className="form-control form-control-sm"
                              value={editForm.duracao_teste}
                              onChange={e => setEditForm({ ...editForm, duracao_teste: e.target.value })} />
                          </div>
                          <div className="col-12">
                            <label className="form-label small mb-1">Observações</label>
                            <input type="text" className="form-control form-control-sm"
                              value={editForm.observacoes}
                              onChange={e => setEditForm({ ...editForm, observacoes: e.target.value })} />
                          </div>

                          {/* Local e Grupo de teste */}
                          <div className="col-md-5">
                            <label className="form-label small mb-1">Local de realização</label>
                            <select className="form-select form-select-sm"
                              value={editForm.id_local}
                              onChange={e => setEditForm({ ...editForm, id_local: e.target.value })}>
                              <option value="">Sem local definido</option>
                              {locais.map(l => (
                                <option key={l.id_local} value={l.id_local}>
                                  {l.nome}{l.tipo ? ` — ${l.tipo}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small mb-1">Grupo de teste</label>
                            <select className="form-select form-select-sm"
                              value={editForm.grupo_teste}
                              onChange={e => setEditForm({ ...editForm, grupo_teste: e.target.value })}>
                              <option value="">—</option>
                              {(GRUPOS_TESTE[editForm._tipo_teste] || []).map(g => (
                                <option key={g.value} value={g.value}>{g.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Equipamento — admin pode trocar; tipo_protetor derivado */}
                          <div className="col-md-6">
                            <label className="form-label small mb-1">Equipamento</label>
                            <select className="form-select form-select-sm"
                              value={editForm.id_equipamento}
                              onChange={e => setEditForm({ ...editForm, id_equipamento: e.target.value })}>
                              <option value="">—</option>
                              {equipamentos
                                .filter(eq => eq.tipo_teste === editForm._tipo_teste)
                                .map(eq => (
                                  <option key={eq.id_equipamento} value={eq.id_equipamento}>
                                    {eq.ca} — {eq.tipo_mascara}{eq.fabricante ? ` (${eq.fabricante})` : ''}
                                  </option>
                                ))}
                            </select>
                            {/* Exibe tipo_protetor do equipamento selecionado */}
                            {editForm._tipo_teste === 'auditivo' && editForm.id_equipamento && (() => {
                              const eq = equipamentos.find(e => e.id_equipamento === editForm.id_equipamento);
                              return eq?.tipo_protetor_auricular ? (
                                <div className="mt-1 small text-muted">
                                  <i className="bi bi-shield-check me-1"></i>
                                  Protetor: <strong>{eq.tipo_protetor_auricular}</strong>
                                </div>
                              ) : null;
                            })()}
                          </div>

                          {/* Campos auditivo — medições de resultado */}
                          {!isRespiratorio(editForm._tipo_teste) && (
                            <>
                              <div className="col-12">
                                <div className="small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '.7rem' }}>
                                  <i className="bi bi-ear me-1"></i>Auditivo — medições
                                </div>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Exposição dB(A)</label>
                                <input type="number" step="0.1" className="form-control form-control-sm"
                                  value={editForm.exposicao_db}
                                  onChange={e => setEditForm({ ...editForm, exposicao_db: e.target.value })} />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">NAP</label>
                                <input type="number" step="0.1" className="form-control form-control-sm"
                                  value={editForm.nap_resultante}
                                  onChange={e => setEditForm({ ...editForm, nap_resultante: e.target.value })} />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small mb-1">Nível de atenuação</label>
                                <select className="form-select form-select-sm"
                                  value={editForm.nivel_atenuacao}
                                  onChange={e => setEditForm({ ...editForm, nivel_atenuacao: e.target.value })}>
                                  <option value="">—</option>
                                  {NIVEIS_ATENUACAO.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </div>
                            </>
                          )}

                          {/* Campos respiratório */}
                          {isRespiratorio(editForm._tipo_teste) && (
                            <>
                              <div className="col-12">
                                <div className="small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '.7rem' }}>
                                  <i className="bi bi-lungs me-1"></i>Respiratório
                                </div>
                              </div>
                              <div className="col-md-4 d-flex align-items-center gap-3">
                                <div className="form-check mb-0">
                                  <input className="form-check-input" type="checkbox" id="ed_brigada"
                                    checked={editForm.faz_parte_brigada}
                                    onChange={e => setEditForm({ ...editForm, faz_parte_brigada: e.target.checked })} />
                                  <label className="form-check-label small" htmlFor="ed_brigada">Brigada</label>
                                </div>
                                <div className="form-check mb-0">
                                  <input className="form-check-input" type="checkbox" id="ed_poco"
                                    checked={editForm.faz_parte_controle_poco}
                                    onChange={e => setEditForm({ ...editForm, faz_parte_controle_poco: e.target.checked })} />
                                  <label className="form-check-label small" htmlFor="ed_poco">Controle de poço</label>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="col-12 d-flex gap-2 mt-1">
                            <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary"
                              onClick={() => setEditId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao {...pagProps} />
      </div>
    </>
  );
}
